export type SubscriptionTier = "essentials" | "professional" | "growth" | "firm";
export type SubscriptionStatus = "trialing" | "active" | "past_due" | "canceled" | "none";
export type BillingCycle = "monthly" | "annual";

export type Feature =
  | "crm_pipeline"
  | "revenue_dashboard"
  | "commission_forecasting"
  | "protection_tracking"
  | "advanced_analytics"
  | "automation_workflows"
  | "pipeline_forecasting"
  | "voip_integration"
  | "multi_adviser_dashboard"
  | "team_management"
  | "adviser_comparison"
  | "compliance_alerts"
  | "introducer_hub"
  | "network_dashboard"
  | "data_room"
  | "custom_reporting"
  | "ai_assistants"
  | "regdata_ledger"
  | "document_storage"
  | "email_integration";

export const TIER_FEATURES: Record<SubscriptionTier, Feature[]> = {
  essentials: [
    "crm_pipeline", "revenue_dashboard", "commission_forecasting", "protection_tracking",
    "advanced_analytics", "automation_workflows", "pipeline_forecasting", "voip_integration",
    "ai_assistants", "regdata_ledger", "introducer_hub", "document_storage", "email_integration",
  ],
  professional: [
    "crm_pipeline", "revenue_dashboard", "commission_forecasting", "protection_tracking",
    "advanced_analytics", "automation_workflows", "pipeline_forecasting", "voip_integration",
    "ai_assistants", "regdata_ledger", "introducer_hub", "document_storage", "email_integration",
    "custom_reporting",
  ],
  growth: [
    "crm_pipeline", "revenue_dashboard", "commission_forecasting", "protection_tracking",
    "advanced_analytics", "automation_workflows", "pipeline_forecasting", "voip_integration",
    "ai_assistants", "regdata_ledger", "introducer_hub", "document_storage", "email_integration",
    "custom_reporting", "data_room",
  ],
  firm: [
    "crm_pipeline", "revenue_dashboard", "commission_forecasting", "protection_tracking",
    "advanced_analytics", "automation_workflows", "pipeline_forecasting", "voip_integration",
    "multi_adviser_dashboard", "team_management", "adviser_comparison", "compliance_alerts",
    "introducer_hub", "network_dashboard", "data_room", "custom_reporting",
    "ai_assistants", "regdata_ledger", "document_storage", "email_integration",
  ],
};

export const TIER_LIMITS: Record<SubscriptionTier, { max_advisers: number; max_clients: number }> = {
  essentials: { max_advisers: 1, max_clients: 99999 },
  professional: { max_advisers: 1, max_clients: 99999 },
  growth: { max_advisers: 1, max_clients: 99999 },
  firm: { max_advisers: 999, max_clients: 99999 },
};

export const TIER_PROPERTY_CREDITS: Record<SubscriptionTier, number> = {
  essentials: 50,
  professional: 150,
  growth: 200,
  firm: 200,
};

// Usage-based pricing (GBP)
export const USAGE_PRICING = {
  id_check: 0.70,                   // per standalone ID verification check
  aml_check: 1.30,                  // per standalone AML screening
  id_aml_check: 1.80,               // per combined ID + AML check
  property_search_overage: 1.50,     // per search beyond allowance
  sms_outbound: 0.08,               // per SMS message
  video_meeting_session: 3.00,       // per session (transcription + analysis)
  additional_portal: 5.00,           // per extra client portal per month
  whatsapp_message: 0.06,
  voip_call_per_minute: 0.05,
  video_meeting_per_minute: 0.03,
} as const;

// Plan pricing config (amounts in GBP per licence)
export const PLAN_PRICING = {
  essentials: { monthly: 49, annual: 492, includedAdvisers: 1 },
  professional: { monthly: 79, annual: 792, includedAdvisers: 1 },
  growth: { monthly: 99, annual: 996, includedAdvisers: 1 },
  firm: { monthly: 89, annual: 888, includedAdvisers: 2, includedSeats: 2, seatPrice: 89, seatPriceAnnual: 888 },
} as const;

// Legacy alias — "solo" maps to "professional" for backward compat
export const LEGACY_TIER_MAP: Record<string, SubscriptionTier> = { solo: "professional" };
export function resolveTier(raw: string): SubscriptionTier {
  if (raw in LEGACY_TIER_MAP) return LEGACY_TIER_MAP[raw];
  return (raw in TIER_LIMITS ? raw : "essentials") as SubscriptionTier;
}

// Legacy alias for backward compat
export const STRIPE_PRICES = PLAN_PRICING as any;

// Internal provider costs — used by the Cost Dashboard for margin analysis
export const INTERNAL_UNIT_COSTS = {
  id_check: 0.12,             // Provider cost per ID check
  aml_check: 0.17,            // Provider cost per AML check
  property_search: 0.051,     // £51 per 1000 searches
  sourcing_brain_licence: 48.00, // Monthly licence per firm
  twenty7tec_api: 250.00,     // Monthly flat rate
  sms_outbound: 0.04,         // Twilio SMS UK
  whatsapp_message: 0.03,     // Twilio WhatsApp session
  voip_call_per_minute: 0.015, // Twilio Voice per min
  video_meeting_per_minute: 0.004, // Twilio Video per min
} as const;

// Add-on pricing (GBP, retail prices)
export const ADDON_PRICING = {
  whatsapp_channel: { monthly: 25, label: "WhatsApp Business Channel" },
  mortgage_sourcing: { perUser: 65, label: "Mortgage Sourcing" },
  api_access: { monthly: 49, label: "API Access" },
} as const;

export const TIER_DETAILS: Record<SubscriptionTier, {
  name: string;
  price: string;
  priceAmount: number;
  annualPrice: string;
  annualPriceAmount: number;
  description: string;
  priceId: string;
  annualPriceId: string;
  productId: string;
}> = {
  essentials: {
    name: "Essentials",
    price: "£49/mo",
    priceAmount: 49,
    annualPrice: "£492/yr",
    annualPriceAmount: 492,
    description: "For solo advisers. No restrictions. Unlimited clients.",
    priceId: "",
    annualPriceId: "",
    productId: "",
  },
  professional: {
    name: "Professional",
    price: "£79/mo",
    priceAmount: 79,
    annualPrice: "£792/yr",
    annualPriceAmount: 792,
    description: "For solo advisers who need more power. Single licence.",
    priceId: "",
    annualPriceId: "",
    productId: "",
  },
  growth: {
    name: "Growth",
    price: "£99/mo",
    priceAmount: 99,
    annualPrice: "£996/yr",
    annualPriceAmount: 996,
    description: "The complete AI platform for solo advisers. No limits.",
    priceId: "",
    annualPriceId: "",
    productId: "",
  },
  firm: {
    name: "Firm",
    price: "£89/mo",
    priceAmount: 89,
    annualPrice: "£888/yr",
    annualPriceAmount: 888,
    description: "For firms with 2 or more people. Volume pricing.",
    priceId: "",
    annualPriceId: "",
    productId: "",
  },
};

export const FEATURE_LABELS: Record<Feature, string> = {
  crm_pipeline: "CRM + Pipeline",
  revenue_dashboard: "Revenue Dashboard",
  commission_forecasting: "Commission Forecasting",
  protection_tracking: "Protection Tracking",
  advanced_analytics: "Advanced Analytics",
  automation_workflows: "Automation Workflows",
  pipeline_forecasting: "Pipeline Forecasting",
  voip_integration: "VoIP Integration",
  multi_adviser_dashboard: "Multi-Adviser Dashboard",
  team_management: "Team Management",
  adviser_comparison: "Adviser Comparison",
  compliance_alerts: "Compliance Alerts",
  introducer_hub: "Introducer Hub",
  network_dashboard: "Network Dashboard",
  data_room: "Data Room",
  custom_reporting: "Custom Reporting",
  ai_assistants: "AI Assistants",
  regdata_ledger: "FCA RegData Ledger",
  document_storage: "Document Storage",
  email_integration: "Email & Communications",
};

// Firm plan helpers
export function calculateFirmTotal(licences: number, cycle: BillingCycle): number {
  if (cycle === "monthly") {
    return licences * TIER_DETAILS.firm.priceAmount;
  }
  return licences * TIER_DETAILS.firm.annualPriceAmount;
}

export function calculateFirmMonthlyEquivalent(licences: number, cycle: BillingCycle): number {
  if (cycle === "monthly") return calculateFirmTotal(licences, cycle);
  return Math.round(calculateFirmTotal(licences, cycle) / 12);
}

// Map route paths to required features
export const ROUTE_FEATURE_MAP: Record<string, Feature> = {
  "/analytics": "advanced_analytics",
  "/automations": "automation_workflows",
  "/ai-intelligence": "voip_integration",
  "/adviser-performance": "adviser_comparison",
  "/network": "network_dashboard",
  "/data-room": "data_room",
  "/multi-adviser": "multi_adviser_dashboard",
  "/compliance-alerts": "compliance_alerts",
  "/custom-reporting": "custom_reporting",
  "/ai-assistants": "ai_assistants",
};

// Map sidebar nav items to required features
export const NAV_FEATURE_MAP: Record<string, Feature> = {
  // Sidebar titles
  Analytics: "advanced_analytics",
  Dashboard: "advanced_analytics",
  Insights: "voip_integration",
  Automations: "automation_workflows",
  "Introducer Analytics": "introducer_hub",
  Compliance: "compliance_alerts",
  "Compliance Alerts": "compliance_alerts",
  "Data Room": "data_room",
  "Custom Reporting": "custom_reporting",
  "AI Assistants": "ai_assistants",
  Network: "network_dashboard",
  "Network Dashboard": "network_dashboard",
  "FCA RegData Ledger": "regdata_ledger",
  Forecasting: "commission_forecasting",
  Documents: "document_storage",
  Communications: "email_integration",
  // Not in sidebar but kept for legacy references
  "Adviser Performance": "adviser_comparison",
  "Multi-Adviser Dashboard": "multi_adviser_dashboard",
};
